<template>
  <div class="slider-container" @mouseenter="stopAutoSlide" @mouseleave="startAutoSlide">
    <div class="slider">
      <!-- Fading backgrounds -->
      <transition name="fade" mode="out-in">
        <div :key="currentIndex" class="slide-bg"
          :style="{ backgroundImage: `url(${slides[currentIndex].backgroundImage})` }"></div>
      </transition>

      <div class="slider-overlay">
        <div class="slider-content">
          <h1 class="slider-title leading-tight font-medium">{{ slides[currentIndex].title }}</h1>
          <p class="slider-subtitle">{{ slides[currentIndex].subtitle }}</p>
          <button v-if="slides[currentIndex].buttonText" class="slider-btn"
            @click="handleButtonClick(slides[currentIndex].buttonLink)">
            {{ slides[currentIndex].buttonText }}
          </button>
        </div>
      </div>

      <!-- Navigation Buttons -->
      <button v-if="slides.length > 1" class="nav-btn prev" @click="prevSlide">
        <img src="@/assets/images/nav_left.svg" alt="Previous" />
      </button>
      <button v-if="slides.length > 1" class="nav-btn next" @click="nextSlide">
        <img src="@/assets/images/nav_right.svg" alt="Next" />
      </button>

      <!-- Pagination Circles -->
      <div v-if="slides.length > 1" class="pagination">
        <span v-for="(slide, index) in slides" :key="index" :class="['dot', { active: index === currentIndex }]"
          @click="goToSlide(index)"></span>
      </div>
    </div>
  </div>
</template>


<script>
import mixin from '@/mixin';
import Address from '@/services/Address';

export default {
  name: "SliderControl",
  mixins: [mixin],
  props: {
    slides: {
      type: Array,
      required: true,
      default: () => [
        {
          backgroundImage: "",
          title: "",
          subtitle: "",
          buttonText: "",
          buttonLink: "",
        },
      ],
    },
    autoSlideInterval: {
      type: Number,
      default: 5, // default 5 seconds
    },
  },
  data() {
    return {
      currentIndex: 0,
      autoTimer: null,
    };
  },
  mounted() {
    this.startAutoSlide();
  },
  beforeUnmount() {
    this.stopAutoSlide();
  },
  methods: {
    startAutoSlide() {
      this.stopAutoSlide(); // clear any existing timer
      if (this.autoSlideInterval > 0) {
        this.autoTimer = setInterval(() => {
          this.nextSlide();
        }, this.autoSlideInterval * 1000);
      }
    },
    stopAutoSlide() {
      if (this.autoTimer) {
        clearInterval(this.autoTimer);
        this.autoTimer = null;
      }
    },
    nextSlide() {
      this.currentIndex = (this.currentIndex + 1) % this.slides.length;
      this.startAutoSlide(); // reset timer on manual navigation
    },
    prevSlide() {
      this.currentIndex =
        (this.currentIndex - 1 + this.slides.length) % this.slides.length;
      this.startAutoSlide(); // reset timer on manual navigation
    },
    goToSlide(index) {
      this.currentIndex = index;
      this.startAutoSlide(); // reset timer on manual navigation
    },
    handleButtonClick(link) {
      if (!link) return;

      // Get credentials safely
      const credentials = this.$store?.state?.credentials || {};
      // console.log('data: ', this.$store.state.credentials);

      if (link.startsWith("http")) {
        const fname = encodeURIComponent(credentials.first_name || "");
        const mname = encodeURIComponent(credentials.middle_name || "");
        const lname = encodeURIComponent(credentials.last_name || "");
        const email = encodeURIComponent(credentials.email || "");
        const mobile = encodeURIComponent(credentials.mobile || "");
        const age = encodeURIComponent(credentials.age || "");
        const gender = encodeURIComponent(credentials.gender || "");
        const account_no = encodeURIComponent(credentials.account_no || "");
        const membership_type = encodeURIComponent(credentials.membership_type || "");
        const uuid = encodeURIComponent(credentials.uuid || "");

        const urlWithParams = `${link}${link.includes("?") ? "&" : "?"}fname=${fname}&mname=${mname}&lname=${lname}&email=${email}&mobile=${mobile}&age=${age}&gender=${gender}&account_no=${account_no}&membership_type=${membership_type}&uuid=${uuid}`;

        window.open(urlWithParams, "_blank");
      } else {
        // Internal: navigate normally
        this.$router.push(link);
      }
    }


  },
};
</script>

<style scoped>
.slider-container {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.slider {
  position: relative;
  width: 100%;
  height: 450px;
  overflow: hidden;
  border-radius: 10px;
}

.slide-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slider-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
}

/* ✅ Centered content */
.slider-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  max-width: 600px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.slider-title {
  font-size: 3rem;
  margin: 0 0 10px;
}

.slider-subtitle {
  font-size: 1.1rem;
  margin: 1px 0 40px 0;
}

.slider-btn {
  padding: 10px 20px;
  background-color: #c09000;
  border: none;
  border-radius: 5px;
  color: white;
  cursor: pointer;
  font-size: 1rem;
  transition: background 0.3s ease;
}

.slider-btn:hover {
  background-color: #016B00;
}

/* Navigation Buttons */
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.7);
  border: none;
  cursor: pointer;
  padding: 3px;
  border-radius: 50%;
  transition: background 0.3s ease;
}

.nav-btn:hover {
  background: white;
}

.prev {
  left: 15px;
}

.next {
  right: 15px;
}

/* Pagination */
.pagination {
  position: absolute;
  bottom: 15px;
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 10px;
}

.dot {
  width: 12px;
  height: 12px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dot.active {
  background: white;
  transform: scale(1.2);
}

/* Responsive */
@media (max-width: 768px) {
  .slider {
    height: 50vh;
  }

  /* ✅ Still center content on mobile */
  .slider-content {
    max-width: 90%;
    padding: 0 10px;
    text-align: center;
  }

  .slider-title {
    font-size: 1.25rem;
    padding: 0 25px;
  }

  .slider-subtitle {
    font-size: 0.8rem;
    margin: 10px 0 35px;
    padding: 0 25px;

  }

  .slider-btn {
    padding: 8px 16px;
    font-size: 0.9rem;
  }

  .nav-btn {
    position: absolute;
    padding: 1px;

  }

}
</style>
